import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { supabase } from '../../lib/supabase';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for individual loan officers',
      monthlyPrice: 29,
      annualPrice: 290,
      features: [
        'Up to 50 guidelines',
        'County assessor directory access',
        'Basic templates',
        'Email support',
        'Mobile access',
        'Basic filtering options',
        '14-day free trial'
      ],
      popular: false,
      buttonText: 'Start Free Trial'
    },
    {
      name: 'Enterprise',
      description: 'For mortgage teams and brokerages',
      monthlyPrice: 99,
      annualPrice: 990,
      features: [
        'Unlimited guidelines',
        'County assessor directory access',
        'Custom templates',
        '24/7 priority support',
        'Team collaboration',
        'API access',
        'Custom integrations',
        'Advanced analytics',
        'Dedicated account manager'
      ],
      popular: true,
      buttonText: 'Start Free Trial'
    }
  ];

  const handleStartTrial = async (planName: string) => {
    try {
      setLoadingPlan(planName);
      
      console.log('Starting trial for plan:', planName);
      
      // Find the plan details
      const selectedPlan = plans.find(plan => plan.name === planName);
      if (!selectedPlan) {
        throw new Error('Plan not found');
      }
      
      const planPrice = billingCycle === 'monthly' ? selectedPlan.monthlyPrice : selectedPlan.annualPrice;
      
      console.log('Sending data:', { planName, planPrice });
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planName: planName,
          planPrice: planPrice
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }

      console.log('Checkout session response:', data);

      if (data?.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        // Use window.open with _self to ensure redirect works
        window.open(data.url, '_self');
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(`Payment setup failed: ${error.message}. Please try again.`);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Get instant access to comprehensive loan guidelines from all major agencies. 
            Start with a 14-day free trial, no credit card required.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-12">
            <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`ml-3 ${billingCycle === 'annual' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Annual
            </span>
            <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Save 17%
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-2xl border-2 p-8 w-full ${
                  plan.popular
                    ? 'border-teal-500 bg-teal-50 shadow-xl lg:scale-105'
                    : 'border-gray-200 bg-white shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex items-center rounded-full bg-teal-500 px-4 py-1 text-sm font-medium text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice}
                    </span>
                    <span className="text-gray-600 ml-1">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                    {billingCycle === 'annual' && (
                      <div className="text-sm text-green-600 mt-1">
                        ${((plan.monthlyPrice * 12 - plan.annualPrice) / 12).toFixed(0)}/month savings
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleStartTrial(plan.name)}
                    disabled={loadingPlan === plan.name}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      plan.popular
                        ? 'bg-teal-500 text-white hover:bg-teal-600 disabled:bg-teal-300'
                        : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
                    }`}
                  >
                    {loadingPlan === plan.name ? (
                      <div className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Processing...
                      </div>
                    ) : (
                      plan.buttonText
                    )}
                  </button>
                </div>
                
                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <i className="ri-check-line text-green-500 text-lg mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {/* Debug info - remove this after confirming all plans show */}
          <div className="mt-8 text-center text-sm text-gray-500">
            Showing {plans.length} plans: {plans.map(p => p.name).join(', ')}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How does the free trial work?
              </h3>
              <p className="text-gray-600">
                Start with a 14-day free trial of any plan. No credit card required. 
                Access all features and cancel anytime during the trial period.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                How often are guidelines updated?
              </h3>
              <p className="text-gray-600">
                Our team monitors all agency websites daily and updates guidelines within 24 hours 
                of any official changes. Professional and Team subscribers get real-time notifications.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Can I change plans anytime?
              </h3>
              <p className="text-gray-600">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect 
                immediately, and billing is prorated accordingly.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Is there a setup fee?
              </h3>
              <p className="text-gray-600">
                No setup fees, no hidden costs. Pay only for your subscription. 
                Team plans include free onboarding and training.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to streamline your loan process?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of mortgage professionals who trust LoanGuide Pro
          </p>
          <button
            onClick={() => handleStartTrial('Enterprise')}
            disabled={loadingPlan === 'Enterprise'}
            className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:bg-gray-100"
          >
            {loadingPlan === 'Enterprise' ? (
              <div className="flex items-center">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Processing...
              </div>
            ) : (
              'Start Your Free Trial'
            )}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
